/** Board-context source capture approved by the owner on 2026-10-12: “we have
 * no record of which list - we need to have it”. */
export type TapEntry = {
  stake_id: number | null;
  event_id: string | null;
  route: string | null;
  list: string | null;
  promo_tab: string | null;
  container: string[];
  super_boost?: boolean;
  factor?: number;
  boosted_factor?: number;
  super_tip?: boolean;
  is_live?: boolean;
  sport_id?: number;
  champ_id?: number;
  country_id?: number;
  /** A promo card's own legs (`event.S[].Id`): the card adds them all at once. */
  card_stake_ids?: number[];
};

export type RouterHistory = { push: (to: string) => void; location: { pathname: string; search?: string } };

// ⛔ The walk is up to 20,000 fibers, and the poll asks every 5 s. The router's
// history object is stable for a mount and its `location` is read live, so it is
// found once per shadow root and re-found only when it stops answering.
const historyCache = new WeakMap<ShadowRoot, RouterHistory>();

/** Finds Digitain's in-memory router, not the wrapper page URL. */
export function findRouterHistory(root: ShadowRoot | null): RouterHistory | null {
  if (!root) return null;
  const cached = historyCache.get(root);
  if (cached && typeof cached.location?.pathname === "string") return cached;
  const found = walkForHistory(root);
  if (found) historyCache.set(root, found);
  return found;
}

function walkForHistory(root: ShadowRoot): RouterHistory | null {
  try {
    const mount = Array.from(root.children).find((e) =>
      Object.keys(e).some((k) => k.indexOf("__reactContainer") === 0),
    );
    if (!mount) return null;
    const key = Object.keys(mount).find((k) => k.indexOf("__reactContainer") === 0);
    if (!key) return null;
    const queue: Array<Record<string, unknown> | null | undefined> = [
      (mount as unknown as Record<string, Record<string, unknown>>)[key],
    ];
    for (let seen = 0; queue.length > 0 && seen < 20000; seen++) {
      const fiber = queue.shift();
      if (!fiber) continue;
      const props = fiber.memoizedProps as { value?: unknown } | undefined;
      const value = props?.value as Record<string, unknown> | undefined;
      if (value && typeof value.push === "function" && value.location
        && typeof (value.location as { pathname?: unknown }).pathname === "string") {
        return value as unknown as RouterHistory;
      }
      queue.push(fiber.child as Record<string, unknown> | null);
      queue.push(fiber.sibling as Record<string, unknown> | null);
    }
  } catch { /* Digitain fiber internals are optional board context. */ }
  return null;
}

export function readRouterLocation(root: ShadowRoot | null): { path: string; search: string } | null {
  const history = findRouterHistory(root);
  if (!history) return null;
  return { path: history.location.pathname, search: history.location.search ?? "" };
}

function firstClass(el: Element): string | null {
  return el.classList.length ? el.classList[0] : null;
}

function eventId(value: unknown): string | null {
  return typeof value === "string" || typeof value === "number" ? String(value) : null;
}

/** Reads only the nearest fiber values: stale outer props must not relabel a tap. */
export function tapEntryFrom(target: Element, root: ShadowRoot): TapEntry | null {
  let stakeId: number | null = null;
  let foundStake = false;
  let foundEvent = false;
  let gotEvent: string | null = null;
  const widget = target.closest(".dg_home_widget");
  const result: TapEntry = {
    // ⛔ null when the router cannot be read — never a guessed route.
    stake_id: null, event_id: null, route: readRouterLocation(root)?.path ?? null,
    list: null, promo_tab: null, container: [],
  };
  try {
    let fiber: Record<string, unknown> | null = null;
    const fiberKey = Object.keys(target).find((k) => k.indexOf("__reactFiber$") === 0);
    if (fiberKey) fiber = (target as unknown as Record<string, Record<string, unknown>>)[fiberKey] ?? null;
    for (let level = 0; fiber && level < 40; level++, fiber = fiber.return as Record<string, unknown> | null) {
      const p = fiber.memoizedProps as Record<string, unknown> | undefined;
      if (!p) continue;
      if (!foundStake && typeof p.stakeId === "number") { stakeId = p.stakeId; foundStake = true; }
      if (!foundEvent) {
        // ⛔ A promo card's `event.Id` is a card UUID; `EId` is the match (read off
        // parikara.com 2026-10-12: Id "b988a331-…", EId 40793742).
        const ev = p.event as { Id?: unknown; EId?: unknown } | undefined;
        const id = eventId(p.eventId) ?? eventId(ev?.EId) ?? eventId(ev?.Id);
        if (id != null) { gotEvent = id; foundEvent = true; }
      }
      const cardStakes = (p.event as { S?: unknown } | undefined)?.S;
      if (result.card_stake_ids === undefined && Array.isArray(cardStakes)) {
        const ids = cardStakes.map((x) => (x as { Id?: unknown } | null)?.Id)
          .filter((x): x is number => typeof x === "number").slice(0, 32);
        if (ids.length) result.card_stake_ids = ids;
      }
      if (result.super_boost === undefined && typeof p.isSuperBoost === "boolean") result.super_boost = p.isSuperBoost;
      if (result.factor === undefined && typeof p.factor === "number") result.factor = p.factor;
      if (result.boosted_factor === undefined && typeof p.boostedFactor === "number") result.boosted_factor = p.boostedFactor;
      const data = p.urlData as Record<string, unknown> | undefined;
      if (data) {
        if (!foundEvent) { const id = eventId(data.eventId); if (id != null) { gotEvent = id; foundEvent = true; } }
        if (result.super_tip === undefined && typeof data.isSuperTip === "boolean") result.super_tip = data.isSuperTip;
        if (result.is_live === undefined && typeof data.isLive === "boolean") result.is_live = data.isLive;
        if (result.sport_id === undefined && typeof data.sportId === "number") result.sport_id = data.sportId;
        if (result.champ_id === undefined && typeof data.champId === "number") result.champ_id = data.champId;
        if (result.country_id === undefined && typeof data.countryId === "number") result.country_id = data.countryId;
      }
    }
  } catch { return null; }
  if (!foundStake && !foundEvent) return null;
  result.stake_id = stakeId;
  result.event_id = gotEvent;
  result.list = widget ? firstClass(widget) : null;
  if (widget && target.closest(".dg_promotion_widgets")) {
    const tab = widget.querySelector(".dg_promotion_widgets_header_tab.tab_active");
    result.promo_tab = tab?.textContent?.trim() || null;
  }
  for (let el: Element | null = target; el; el = el.parentElement) {
    const name = firstClass(el);
    if (name && result.container[result.container.length - 1] !== name) result.container.push(name);
    if (result.container.length >= 12 || el.parentNode === root) break;
  }
  return result;
}
